import { Tabs } from 'antd';
import React, { FC, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { ConversationAPI } from 'api/conversations';
import { TaskId } from 'api/models';
import { TaskSourceType } from 'api/tasks';
import { ConversationPane } from 'components/organisms/conversations';
import { Task } from 'data/tasks';
import { TaskDrawerTab, useNavigateToTask } from 'hooks/use-navigate-to-task';
import { useRequestTaskApproval } from 'hooks/use-request-task-approval';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { TaskApprovals } from '../task-approvals';
import styles from './secondary-tabs.module.css';
import { useTaskDrawerTab } from './use-task-drawer-tab';

const { TabPane } = Tabs;

const StyledTabs = styled(Tabs)<{ canRenderApprovalTab: boolean }>`
  .ant-tabs-nav-list .ant-tabs-tab {
    border-bottom: 0;
  }
  .ant-tabs-ink-bar {
    height: ${({ canRenderApprovalTab }) =>
      `${!canRenderApprovalTab && '0'}px`};
  }
`;

interface SecondaryTabsProps {
  data: Task;
}

export const SecondaryTabs: FC<SecondaryTabsProps> = (props) => {
  const { data } = props;

  const { taskId, project, private: taskIsPrivate, source } = data;

  const isDocumentTask =
    source?.sourceType != null &&
    [TaskSourceType.DocumentSection, TaskSourceType.Document].includes(
      source?.sourceType,
    );
  const currentUser = useUser();
  const navigateToTask = useNavigateToTask();
  const [params, setTab] = useTaskDrawerTab();
  const [taskApprovalParams] = useRequestTaskApproval();
  const conversationTitle = useTranslation('conversations');
  const translatedApprovalsText = useTranslation('approvals');
  const approvals = data?.approvals ?? [];

  const canShowNewTaskApproval = taskApprovalParams[0] === 'show';
  const activeTab = params[1];
  const hasApprovals = (approvals ?? []).length > 0;
  const isApprovalTab = activeTab === TaskDrawerTab.Approvals;
  const canRenderApprovalTab = isApprovalTab || hasApprovals;

  const navigateTo = useCallback(
    (tab: TaskDrawerTab) => navigateToTask(TaskId(project, taskId), { tab }),
    [navigateToTask, taskId, project],
  );

  const navigateToApprovals = useCallback(() => {
    return navigateTo(TaskDrawerTab.Approvals);
  }, [navigateTo]);

  const navigateToConversations = useCallback(() => {
    return navigateTo(TaskDrawerTab.Conversations);
  }, [navigateTo]);

  const handleCancel = useCallback(() => {
    return hasApprovals ? navigateToApprovals() : navigateToConversations();
  }, [hasApprovals, navigateToApprovals, navigateToConversations]);

  const handleSubmit = useCallback(() => {
    navigateToApprovals();
  }, [navigateToApprovals]);

  const approvalData = useMemo(() => {
    return canShowNewTaskApproval || !hasApprovals
      ? {
          approvalId: 0,
          requestBy: currentUser.user,
          requestTo: [],
          private: taskIsPrivate,
        }
      : undefined;
  }, [canShowNewTaskApproval, hasApprovals, currentUser, taskIsPrivate]);

  return (
    <StyledTabs
      className={styles.conversation__tabs}
      defaultActiveKey={TaskDrawerTab.Conversations}
      activeKey={activeTab}
      tabBarGutter={1}
      onChange={(key) => setTab(key as TaskDrawerTab)}
      canRenderApprovalTab={canRenderApprovalTab}
    >
      <TabPane tab={conversationTitle} key={TaskDrawerTab.Conversations}>
        <ConversationPane
          context={{ type: 'TASK', id: taskId }}
          projectId={project}
          accountId={currentUser.account.accountId}
          apiService={ConversationAPI}
        />
      </TabPane>
      {canRenderApprovalTab && (
        <TabPane tab={translatedApprovalsText} key={TaskDrawerTab.Approvals}>
          <TaskApprovals
            isPrivate={taskIsPrivate}
            onNewApprovalCancel={handleCancel}
            onNewApprovalSubmit={handleSubmit}
            newData={approvalData}
            data={approvals ?? []}
            taskId={taskId}
            projectId={project}
            isDocumentTask={isDocumentTask}
          />
        </TabPane>
      )}
    </StyledTabs>
  );
};

// eslint-disable-next-line import/no-default-export
export default SecondaryTabs;
